import { ALL_MONTHS } from "consts";
import dayjs, { Dayjs } from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

/** Format for API date params: first/last day of month as YYYY-MM-DD */
const DATE_FORMAT_ISO = "YYYY-MM-DD" as const;

export type MonthBoundary = "first" | "last";

export const getUTCDateFormat = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-DD-YYYY";
  }
  return dateFormat;
};

export const getDateFormat = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-DD-YYYY";
  }
  return dateFormat === "DD-MM-YYYY" ? "D MMM, YYYY" : "MMM D, YYYY";
};

/**
 * Returns a date format string based on a saved user preference in sessionStorage,
 * optionally using a custom separator.
 *
 * If no preference is stored, it defaults to "DD-MM-YYYY" or "MM-DD-YYYY" depending
 * on the format, with the specified separator (if any).
 *
 * @param separator - Optional custom separator to use instead of dashes (e.g., "/", ".").
 * @returns A date format string in either "DD-MM-YYYY" or "MM-DD-YYYY" style with the chosen separator.
 */
export const getDateFormatset = (separator?: string) => {
  let dateFormat = null;
  try {
    const storedFormat = window.sessionStorage.getItem("date_format");
    if (storedFormat) {
      dateFormat = JSON.parse(storedFormat);
    }
  } catch (error) {
    dateFormat = null;
  }

  const customSeparatorDateFormat = separator ? "DD-MM-YYYY".replaceAll("-", separator) : "DD-MM-YYYY";
  const customSeparatorDateFormatMonthBeforeDays = separator ? "MM-DD-YYYY".replaceAll("-", separator) : "MM-DD-YYYY";

  if (dateFormat === null) {
    return customSeparatorDateFormat;
  }
  return dateFormat === "DD-MM-YYYY" ? customSeparatorDateFormat : customSeparatorDateFormatMonthBeforeDays;
};

export const getQuarter = (date: Dayjs) => {
  const month = date.month(); // 0-11
  return Math.floor(month / 3) + 1;
};

/**
 * Gets the fiscal year for a given date based on the fiscal year start month
 * @param date - The date to get the fiscal year for
 * @param fiscalYearStartMonth - The month index (0-11) when the fiscal year starts
 * @returns The fiscal year number
 */
export const getFiscalYearFromDate = (date: Dayjs, fiscalYearStartMonth: number) => {
  const month = date.month(); // 0-11
  const year = date.year();

  if (month < fiscalYearStartMonth) {
    return year;
  }
  return year + 1;
};

/**
 * Gets the fiscal year start month index from company months configuration
 * @param companyMonths - Array with start and end months (e.g., ["May", "Apr"])
 * @returns The month index (0-11) when the fiscal year starts
 */
export const getFiscalYearStartMonthIndex = (companyMonths: string[]): number => {
  const allMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return allMonths.findIndex(m => m.toLowerCase() === companyMonths[0]?.toLowerCase());
};

export const getDateFormatmonthset = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-YYYY";
  }
  return dateFormat === "DD-MM-YYYY" ? "MM-YYYY" : "MM-YYYY";
};

export function isValidISODate(dateString: string) {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
  return isoDateRegex.test(dateString);
}

export const getFormattedDate = (date: string, separator?: string) => {
  if (!date) return null;
  const formattedDate = dayjs(date.substring(0, 10)).format(getDateFormatset());
  if (separator === "/") return formattedDate.replaceAll("-", "/");

  return formattedDate;
};

export const formatDateToISOStartOfDay = (date: Date) =>
  `${date.toISOString().split("T")[0]}T00:00:00Z`;

export const getFirstAndLastDateOfFiscalYear = (
  fiscalYear: number,
  startMonth: string,
  end_Month: string,
) => {
  const adjustedYear = startMonth.toUpperCase() === "JAN" ? fiscalYear : fiscalYear - 1;
  const startDate = dayjs(`${startMonth} 1, ${adjustedYear}`);
  const endDate = dayjs(`${end_Month} 1, ${fiscalYear}`).endOf("month");
  const firstDate = startDate.format("YYYY-MM-DD");
  const lastDate = endDate.format("YYYY-MM-DD");
  return { firstDate, lastDate };
};

/**
 * Formats a date string into a user-friendly representation like "Today", "Yesterday", 
 * or a short date format. Optionally includes the time in HH:mm:ss format.
 *
 * @param dateString - The ISO date string to format.
 * @param showTime - Whether to include the time portion in the output.
 * @returns A formatted string representing the date in a friendly way.
 *
 */
export function formatFriendlyDate(dateString: string, showTime: boolean): string {
  const date = dayjs(dateString).toString().substring(0, 25);

  let formattedString = "";

  if (dayjs(date).isToday()) {
    formattedString = "Today";
  } else if (dayjs(date).add(-1, "day").isYesterday()) {
    formattedString = "Yesterday";
  } else {
    formattedString = dayjs(date).format("ddd MM/DD/YYYY");
  }

  if (showTime && formattedString !== "Today" && formattedString !== "Yesterday") {
    formattedString += ` ${dayjs(date).format("HH:mm:ss")}`;
  } else if (showTime) {
    formattedString += ` ${dayjs(date).format("HH:mm:ss")}`;
  }

  return formattedString;
}

/**
 * Converts a label in the format "Mon 'YY" (e.g., "Jun '24" or "Dec '97") into a Date object 
 * representing the first day of that month. Automatically determines the correct century 
 * based on the year.
 *
 * @param label - A string label with the format "MMM 'YY", where MMM is the month abbreviation
 *                (e.g., "Jan", "Feb", "Mar") and 'YY is the two-digit year.
 * @returns A Date object set to the first day of the parsed month and year.
 *
 */
export const parseLabelToDate = (label: string): Date => {
  const [monthStr, yearShort] = label.split(" ");
  const dateBeforeParsed = new Date(`01-${monthStr}-${yearShort.replace("'", "")}`);
  const month = dateBeforeParsed.getMonth();
  const year = dateBeforeParsed.getFullYear();
  return new Date(year, month);
};


/**
 * Sorts an array of date labels (in the format "MMM 'YY", e.g., "Jun '24" or "Dec '97") 
 * in ascending chronological order.
 *
 * @param dates - An array of strings representing dates in the "MMM 'YY" format.
 * @returns A new array with the same labels sorted from oldest to newest.
 */
export const sortedDates = (dates: string[]) =>
  [...dates].sort((a, b) => parseLabelToDate(a).getTime() - parseLabelToDate(b).getTime());


/**
 * Formats a date string into a ISO 8601 date string in the format "YYYY-MM-DD".
 *
 * @param year - The year of the date.
 * @param monthIndex - The index of the month of the date.
 * @returns A formatted date string in the format "YYYY-MM-DD".
 */
export const formatDate = (year: number, monthIndex: number) =>
  dayjs(`${year}-${monthIndex + 1}-01`).format("YYYY-MM-DD");

const capitalizeString = (str: string): string => {
  if (!str || typeof str !== "string") return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};


/**
 * Calculates the start and end date of a fiscal year based on given months and the current fiscal year.
 *
 * @param months - An array with two elements: the start and end month of the fiscal year (e.g., ["Feb", "Jan"])
 * @param currentFiscalYear - The current fiscal year string (e.g., "2024 Fiscal Year")
 * @returns An object with `fromDate` (start of fiscal year) and `endDate` (end of fiscal year)
 */
export const getFiscalYear = (months: string[], currentFiscalYear: string) => {
  const allMonths = [...ALL_MONTHS];
  const fiscalYear = Number(currentFiscalYear.substring(0, 4));
  const startMonthIndex = allMonths.indexOf(capitalizeString(months[0]));
  const endMonthIndex = allMonths.indexOf(capitalizeString(months[1]));

  const startYear = startMonthIndex <= endMonthIndex ? fiscalYear : fiscalYear - 1;
  const endYear = fiscalYear;

  return {
    fromDate: formatDate(startYear, startMonthIndex),
    endDate: dayjs(formatDate(endYear, endMonthIndex))
      .endOf("month")
      .format("YYYY-MM-DD"),
  };
};

/**
 * Determines whether the difference between two dates exceeds one month.
 *
 * @param {Dayjs} date1 - The starting date.
 * @param {Dayjs} date2 - The ending date.
 * @returns {boolean} `true` if the difference between `date1` and `date2` is greater than one month, otherwise `false`.
 */
export const isMoreThanOneMonthDifference = (date1: Dayjs, date2: Dayjs): boolean => {
  const difference = date2.diff(date1, "month", true);
  return difference > 1;
};

/**
 * Converts a timeframe group string and timeframe type into start and end dates.
 * 
 * @param timeframeGroup - A string representing the timeframe group (e.g., "May - 2024")
 * @param timeframe - The timeframe type ("monthly", "quarterly", "yearly")
 * @param companyMonths - Optional array with start and end months for fiscal year calculation (e.g., ["May", "Apr"])
 * @returns An object with startDate and endDate in YYYY-MM-DD format
 * 
 */
export const getDateRangeFromTimeframe = (
  timeframeGroup: string,
  timeframe: string,
  companyMonths?: string[],
) => {
  let date: Dayjs;
  const fiscalYearStartMonth = companyMonths ? getFiscalYearStartMonthIndex(companyMonths) : 0;

  if (timeframeGroup.includes("Q") && timeframeGroup.includes(" - ")) {
    const [quarterStr, yearStr] = timeframeGroup.split(" - ");
    const quarter = parseInt(quarterStr.replace("Q", ""), 10);
    const year = parseInt(yearStr, 10);

    if (companyMonths) {
      const quarterStartMonth = fiscalYearStartMonth + (quarter - 1) * 3;
      const adjustedQuarterStartMonth = quarterStartMonth % 12;
      const quarterStartYear = (fiscalYearStartMonth === 0 ? year : year - 1) + Math.floor(quarterStartMonth / 12);
      date = dayjs().year(quarterStartYear).month(adjustedQuarterStartMonth).date(1);
    } else {
      const month = (quarter - 1) * 3;
      date = dayjs().year(year).month(month).date(1);
    }
  } else if (timeframeGroup.match(/^\d{4}$/)) {
    const year = parseInt(timeframeGroup, 10);

    if (companyMonths) {
      const startYear = year - 1;
      date = dayjs().year(startYear).month(fiscalYearStartMonth).date(1);
    } else {
      date = dayjs().year(year).month(0).date(1);
    }
  } else {
    date = dayjs(timeframeGroup, "MMM - YYYY");
  }

  switch (timeframe.toLowerCase()) {
    case "monthly":
      return {
        startDate: date.startOf("month").format("YYYY-MM-DD"),
        endDate: date.endOf("month").format("YYYY-MM-DD"),
      };
    case "quarterly":
      if (companyMonths) {
        const fiscalYear = getFiscalYearFromDate(date, fiscalYearStartMonth);
        const monthInFiscalYear = (date.month() - fiscalYearStartMonth + 12) % 12;
        const fiscalQuarter = Math.floor(monthInFiscalYear / 3) + 1;

        const quarterStartMonth = fiscalYearStartMonth + (fiscalQuarter - 1) * 3;
        const adjustedQuarterStartMonth = quarterStartMonth % 12;
        const quarterStartYear = fiscalYear - 1 + Math.floor(quarterStartMonth / 12);

        const quarterStart = dayjs().year(quarterStartYear).month(adjustedQuarterStartMonth).startOf("month");
        const quarterEnd = quarterStart.add(2, "months").endOf("month");

        return {
          startDate: quarterStart.format("YYYY-MM-DD"),
          endDate: quarterEnd.format("YYYY-MM-DD"),
        };
      }
      {
        const quarter = Math.floor(date.month() / 3) + 1;
        const quarterStart = date.month((quarter - 1) * 3).startOf("month");
        const quarterEnd = quarterStart.add(2, "months").endOf("month");
        return {
          startDate: quarterStart.format("YYYY-MM-DD"),
          endDate: quarterEnd.format("YYYY-MM-DD"),
        };
      }
    case "yearly":
      if (companyMonths) {
        const fiscalYear = getFiscalYearFromDate(date, fiscalYearStartMonth);

        if (fiscalYearStartMonth === 0) {
          return {
            startDate: dayjs().year(fiscalYear).month(0).startOf("month").format("YYYY-MM-DD"),
            endDate: dayjs().year(fiscalYear).month(11).endOf("month").format("YYYY-MM-DD"),
          };
        }
        const startYear = fiscalYear - 1;
        const endYear = fiscalYear;

        const fiscalYearStart = dayjs().year(startYear).month(fiscalYearStartMonth).startOf("month");
        const fiscalYearEnd = dayjs().year(endYear).month(fiscalYearStartMonth - 1).endOf("month");

        return {
          startDate: fiscalYearStart.format("YYYY-MM-DD"),
          endDate: fiscalYearEnd.format("YYYY-MM-DD"),
        };
      }
      return {
        startDate: date.startOf("year").format("YYYY-MM-DD"),
        endDate: date.endOf("year").format("YYYY-MM-DD"),
      };
    default:
      return {
        startDate: date.format("YYYY-MM-DD"),
        endDate: date.format("YYYY-MM-DD"),
      };
  }
};

/**
 * Formats a date string to display in audit tooltips with UTC time using user's preferred date format.
 * 
 * @param dateString - The ISO date string to format
 * @returns A formatted string using the user's preferred date format with UTC time, or empty string if dateString is null/empty
 * 
 */
export const formatAuditDate = (dateString: string | null): string => {
  if (!dateString) return "";

  const userDateFormat = getDateFormatset("/");
  return dayjs(dateString).format(`${userDateFormat}, HH:mm UTC`);
};

/**
 * Validates export parameters and returns an error message if invalid, or null if valid.
 * Used by ExportButton's onBeforeExport prop to show validation errors before making API calls.
 * 
 * @param params - The export parameters to validate
 * @returns Error message string if validation fails, null if valid
 */
export const validateExportParams = (params: {
  startDate?: string;
  endDate?: string;
  [key: string]: unknown;
}): string | null => {
  if (!params) {
    return "Export parameters are required";
  }

  if (!params.startDate || !params.endDate) {
    return "Start date and end date are required";
  }

  const startDate = dayjs(params.startDate);
  const endDate = dayjs(params.endDate);

  if (!startDate.isValid()) {
    return "Invalid start date format";
  }

  if (!endDate.isValid()) {
    return "Invalid end date format";
  }

  if (endDate.isBefore(startDate)) {
    return "End date must be after start date";
  }

  return null;
};

/**
 * Returns the first or last day of the month for the given date in "YYYY-MM-DD" format.
 * @param date - Dayjs date (or value that Dayjs can parse)
 * @param boundary - "first" for first day of month, "last" for last day of month
 * @returns Date string in YYYY-MM-DD format
 */
export const getMonthBoundaryFormatted = (date: Dayjs, boundary: MonthBoundary): string => {
  const d = dayjs(date);
  return boundary === "first"
    ? d.startOf("month").format(DATE_FORMAT_ISO)
    : d.endOf("month").format(DATE_FORMAT_ISO);
};
